<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { RefreshCw, Trash2 } from 'lucide-vue-next';
import { computed, inject, ref } from 'vue';
import { snapshot as prismaSnapshot } from '@/routes/projects/prisma';
import type { PrismaSnapshot } from './prisma-extension';

const props = defineProps<NodeViewProps>();

interface PrismaNodeAttrs {
    stats: PrismaSnapshot | null;
    generatedAt: string | null;
    caption: string;
    projectId: string | null;
}

const attrs = computed<PrismaNodeAttrs>(
    () => props.node.attrs as PrismaNodeAttrs,
);

// projectId from attrs (set when inserting) or from editor context
const injectedProjectId = inject<string>('editorProjectId', '');
const projectId = computed<string>(
    () => attrs.value.projectId ?? injectedProjectId,
);

const isLoading = ref(false);
const errorMsg = ref('');
const caption = ref(attrs.value.caption);

// ── Stats ──────────────────────────────────────────────────────────────────

const stats = computed<PrismaSnapshot | null>(() => attrs.value.stats);

function val(n: number | undefined): number {
    return n ?? 0;
}

async function regenerate() {
    if (!projectId.value) {
        errorMsg.value = 'Aucun projet associé à ce nœud.';

        return;
    }

    isLoading.value = true;
    errorMsg.value = '';

    try {
        const res = await fetch(prismaSnapshot.url(projectId.value), {
            headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }

        const data: PrismaSnapshot = await res.json();
        props.updateAttributes({
            stats: data,
            generatedAt: data.generated_at,
            projectId: projectId.value,
        });
    } catch {
        errorMsg.value = 'Impossible de récupérer les données PRISMA.';
    } finally {
        isLoading.value = false;
    }
}

function saveCaption() {
    props.updateAttributes({ caption: caption.value });
}

// ── SVG layout constants ────────────────────────────────────────────────────

const SVG_W = 660;
const SVG_H = 560;

// Box sizes
const BOX_W = 160;
const BOX_H = 52;
const SIDE_W = 140;
const SIDE_H = 46;

// X positions
const LEFT_X = 20; // left column (from databases / from other sources)
const MID_X = 250; // main flow column (centre of box)
const SIDE_X = 460; // exclusion boxes (right)

// Y positions for 4 rows
const ROW_Y = [60, 190, 320, 450];

const COLORS = {
    identification: '#dbeafe', // blue-100
    screening: '#dcfce7', // green-100
    eligibility: '#fef9c3', // yellow-100
    included: '#ede9fe', // violet-100
    side: '#fee2e2', // red-100
    border: '#6b7280', // gray-500
    text: '#111827', // gray-900
    arrow: '#374151', // gray-700
};

type BoxColor =
    | 'identification'
    | 'screening'
    | 'eligibility'
    | 'included'
    | 'side';

function boxColor(c: BoxColor): string {
    return COLORS[c];
}
</script>

<template>
    <NodeViewWrapper
        as="div"
        class="prisma-block node-insert-animate not-prose my-6 rounded-xl border border-border/40 bg-muted/20 p-4 select-none"
        contenteditable="false"
    >
        <!-- Header row -->
        <div class="mb-3 flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
                <span class="text-sm font-semibold text-foreground"
                    >Diagramme PRISMA 2020</span
                >
                <span
                    v-if="attrs.generatedAt"
                    class="text-xs text-muted-foreground"
                >
                    ({{
                        new Date(attrs.generatedAt).toLocaleDateString('fr-FR')
                    }})
                </span>
            </div>
            <template v-if="props.editor?.isEditable">
                <button
                    class="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
                    :disabled="isLoading || !projectId"
                    @click="regenerate"
                >
                    <RefreshCw
                        class="h-3.5 w-3.5"
                        :class="{ 'animate-spin': isLoading }"
                    />
                    Régénérer
                </button>
                <button
                    class="inline-flex items-center rounded p-1 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive"
                    title="Supprimer le diagramme"
                    @click="props.deleteNode()"
                >
                    <Trash2 class="h-3.5 w-3.5" />
                </button>
            </template>
        </div>

        <!-- Error -->
        <p v-if="errorMsg" class="mb-2 text-xs text-destructive">
            {{ errorMsg }}
        </p>

        <!-- No data state -->
        <div
            v-if="!stats"
            class="flex flex-col items-center justify-center gap-3 py-8 text-muted-foreground"
        >
            <p class="text-sm">
                Aucune donnée — cliquez sur « Régénérer » pour charger les
                statistiques.
            </p>
            <button
                class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                :disabled="isLoading || !projectId"
                @click="regenerate"
            >
                <RefreshCw
                    class="h-3.5 w-3.5"
                    :class="{ 'animate-spin': isLoading }"
                />
                Charger les données
            </button>
        </div>

        <!-- PRISMA SVG diagram -->
        <div v-else class="overflow-x-auto">
            <svg
                :width="SVG_W"
                :height="SVG_H"
                viewBox="0 0 660 560"
                xmlns="http://www.w3.org/2000/svg"
                class="mx-auto block max-w-full"
                style="pointer-events: none"
                role="img"
                aria-label="Diagramme PRISMA 2020"
            >
                <defs>
                    <marker
                        id="arrowhead"
                        markerWidth="8"
                        markerHeight="6"
                        refX="8"
                        refY="3"
                        orient="auto"
                    >
                        <polygon points="0 0, 8 3, 0 6" :fill="COLORS.arrow" />
                    </marker>
                </defs>

                <!-- ═══════════════ ROW LABELS (left margin) ═══════════════ -->
                <text
                    x="10"
                    y="76"
                    font-size="9"
                    fill="#6b7280"
                    transform="rotate(-90,10,76)"
                    text-anchor="middle"
                >
                    Identification
                </text>
                <text
                    x="10"
                    y="206"
                    font-size="9"
                    fill="#6b7280"
                    transform="rotate(-90,10,206)"
                    text-anchor="middle"
                >
                    Sélection
                </text>
                <text
                    x="10"
                    y="336"
                    font-size="9"
                    fill="#6b7280"
                    transform="rotate(-90,10,336)"
                    text-anchor="middle"
                >
                    Éligibilité
                </text>
                <text
                    x="10"
                    y="466"
                    font-size="9"
                    fill="#6b7280"
                    transform="rotate(-90,10,466)"
                    text-anchor="middle"
                >
                    Inclusion
                </text>

                <!-- ═══════════════ IDENTIFICATION ROW ═══════════════════════ -->

                <!-- Box: from databases -->
                <rect
                    :x="LEFT_X + 10"
                    :y="ROW_Y[0]"
                    :width="BOX_W"
                    :height="BOX_H"
                    rx="6"
                    :fill="boxColor('identification')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="LEFT_X + 10 + BOX_W / 2"
                    :y="ROW_Y[0] + 18"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Études identifiées
                </text>
                <text
                    :x="LEFT_X + 10 + BOX_W / 2"
                    :y="ROW_Y[0] + 30"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    via bases de données
                </text>
                <text
                    :x="LEFT_X + 10 + BOX_W / 2"
                    :y="ROW_Y[0] + 44"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.identification.from_databases) }}
                </text>

                <!-- Box: from other sources -->
                <rect
                    :x="LEFT_X + 10 + BOX_W + 20"
                    :y="ROW_Y[0]"
                    :width="BOX_W"
                    :height="BOX_H"
                    rx="6"
                    :fill="boxColor('identification')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y="ROW_Y[0] + 18"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Études identifiées
                </text>
                <text
                    :x="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y="ROW_Y[0] + 30"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    autres sources
                </text>
                <text
                    :x="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y="ROW_Y[0] + 44"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.identification.from_other_sources) }}
                </text>

                <!-- Arrows down to merge point -->
                <line
                    :x1="LEFT_X + 10 + BOX_W / 2"
                    :y1="ROW_Y[0] + BOX_H"
                    :x2="LEFT_X + 10 + BOX_W / 2"
                    :y2="ROW_Y[0] + BOX_H + 25"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />
                <line
                    :x1="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y1="ROW_Y[0] + BOX_H"
                    :x2="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y2="ROW_Y[0] + BOX_H + 10"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                />
                <!-- horizontal merge line -->
                <line
                    :x1="LEFT_X + 10 + BOX_W / 2"
                    :y1="ROW_Y[0] + BOX_H + 10"
                    :x2="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y2="ROW_Y[0] + BOX_H + 10"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                />

                <!-- Duplicates removed box (right side of row 0) -->
                <rect
                    :x="SIDE_X"
                    :y="ROW_Y[0] + 5"
                    :width="SIDE_W"
                    :height="SIDE_H"
                    rx="6"
                    :fill="boxColor('side')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[0] + 20"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Doublons supprimés
                </text>
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[0] + 38"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.duplicates_removed) }}
                </text>

                <!-- Arrow from merge to duplicates -->
                <line
                    :x1="LEFT_X + 10 + BOX_W + 20 + BOX_W / 2"
                    :y1="ROW_Y[0] + BOX_H + 10"
                    :x2="SIDE_X - 5"
                    :y2="ROW_Y[0] + 28"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />

                <!-- ═══════════════ SCREENING ROW ═════════════════════════════ -->

                <!-- Box: screened -->
                <rect
                    :x="MID_X"
                    :y="ROW_Y[1]"
                    :width="BOX_W"
                    :height="BOX_H"
                    rx="6"
                    :fill="boxColor('screening')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[1] + 18"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Études criblées
                </text>
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[1] + 44"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.screening.screened) }}
                </text>

                <!-- Arrow from merge to screened box -->
                <line
                    :x1="LEFT_X + 10 + BOX_W / 2"
                    :y1="ROW_Y[0] + BOX_H + 25"
                    :x2="MID_X + BOX_W / 2"
                    :y2="ROW_Y[1]"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />

                <!-- Excluded at screening (right) -->
                <rect
                    :x="SIDE_X"
                    :y="ROW_Y[1] + 3"
                    :width="SIDE_W"
                    :height="SIDE_H"
                    rx="6"
                    :fill="boxColor('side')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[1] + 18"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Exclus (criblage)
                </text>
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[1] + 37"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.screening.excluded) }}
                </text>

                <!-- Arrow from screened to excluded -->
                <line
                    :x1="MID_X + BOX_W"
                    :y1="ROW_Y[1] + BOX_H / 2"
                    :x2="SIDE_X"
                    :y2="ROW_Y[1] + 26"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />

                <!-- ═══════════════ ELIGIBILITY ROW ═══════════════════════════ -->

                <!-- Box: full text assessed -->
                <rect
                    :x="MID_X"
                    :y="ROW_Y[2]"
                    :width="BOX_W"
                    :height="BOX_H"
                    rx="6"
                    :fill="boxColor('eligibility')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[2] + 16"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Texte intégral
                </text>
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[2] + 27"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    évalué
                </text>
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[2] + 44"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.eligibility.full_text_assessed) }}
                </text>

                <!-- Arrow from screened to full text -->
                <line
                    :x1="MID_X + BOX_W / 2"
                    :y1="ROW_Y[1] + BOX_H"
                    :x2="MID_X + BOX_W / 2"
                    :y2="ROW_Y[2]"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />

                <!-- Excluded full text (right) -->
                <rect
                    :x="SIDE_X"
                    :y="ROW_Y[2] + 3"
                    :width="SIDE_W"
                    :height="SIDE_H"
                    rx="6"
                    :fill="boxColor('side')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[2] + 18"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Exclus (texte intégral)
                </text>
                <text
                    :x="SIDE_X + SIDE_W / 2"
                    :y="ROW_Y[2] + 37"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.eligibility.excluded) }}
                </text>

                <!-- Arrow from full text to excluded -->
                <line
                    :x1="MID_X + BOX_W"
                    :y1="ROW_Y[2] + BOX_H / 2"
                    :x2="SIDE_X"
                    :y2="ROW_Y[2] + 26"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />

                <!-- ═══════════════ INCLUDED ROW ═══════════════════════════════ -->

                <!-- Box: included -->
                <rect
                    :x="MID_X"
                    :y="ROW_Y[3]"
                    :width="BOX_W"
                    :height="BOX_H"
                    rx="6"
                    :fill="boxColor('included')"
                    :stroke="COLORS.border"
                    stroke-width="1"
                />
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[3] + 22"
                    font-size="9"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    Études incluses
                </text>
                <text
                    :x="MID_X + BOX_W / 2"
                    :y="ROW_Y[3] + 44"
                    font-size="13"
                    font-weight="bold"
                    text-anchor="middle"
                    :fill="COLORS.text"
                >
                    {{ val(stats.included) }}
                </text>

                <!-- Arrow from full text to included -->
                <line
                    :x1="MID_X + BOX_W / 2"
                    :y1="ROW_Y[2] + BOX_H"
                    :x2="MID_X + BOX_W / 2"
                    :y2="ROW_Y[3]"
                    :stroke="COLORS.arrow"
                    stroke-width="1.5"
                    marker-end="url(#arrowhead)"
                />
            </svg>
        </div>

        <!-- Caption / legend -->
        <div class="mt-3 border-t border-border/30 pt-2">
            <input
                v-model="caption"
                class="w-full rounded border-none bg-transparent text-center text-xs text-muted-foreground italic outline-none focus:bg-muted/30"
                placeholder="Légende du diagramme PRISMA…"
                @blur="saveCaption"
                @keydown.enter.prevent="saveCaption"
            />
        </div>
    </NodeViewWrapper>
</template>
